import { useState } from 'react';
import { ArrowLeft, Plus, Trash2, ChevronRight } from 'lucide-react';
import { cn } from '../../lib/utils';
import { chatTheme as th } from '../../theme/chat';

export default function ItemSidebar({
  title,
  items = [],
  activeId,
  onSelectItem,
  onDeleteItem,
  templates,
  onSelectTemplate,
  extraContent,
  chat,
  onChatOpen,
  onChatClose,
  chatItemName,
}) {
  const [mode, setMode] = useState('list');

  const handleItemClick = (id) => {
    onSelectItem?.(id);
    setMode('chat');
    onChatOpen?.(id);
  };

  const handleTemplateClick = (type) => {
    onSelectTemplate?.(type);
    setMode('chat');
    onChatOpen?.(null);
  };

  const handleNewChat = () => {
    setMode('chat');
    onChatOpen?.(null);
  };

  const handleBack = () => {
    setMode('list');
    onChatClose?.();
  };

  if (mode === 'chat') {
    return (
      <div className={cn(th.page.sidebarWidth, 'flex-shrink-0 bg-white border-r border-zinc-200 flex flex-col h-full')}>
        {/* Back header */}
        <div className="flex items-center gap-2 h-11 px-3 border-b border-zinc-200 flex-shrink-0">
          <button
            onClick={handleBack}
            className="flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-800 transition-colors"
          >
            <ArrowLeft size={14} />
            <span>Back</span>
          </button>
          <span className="text-xs text-zinc-300">|</span>
          <span className="text-xs font-medium text-zinc-700 truncate flex-1">
            {chatItemName || 'New Chat'}
          </span>
        </div>

        {/* Chat content */}
        <div className="flex-1 overflow-hidden">
          {chat}
        </div>
      </div>
    );
  }

  return (
    <div className={cn(th.page.sidebarWidth, 'flex-shrink-0 bg-white border-r border-zinc-200 flex flex-col h-full')}>
      {/* Templates */}
      {templates?.length > 0 && (
        <div className="px-3 pt-3 pb-1">
          <div className="text-[10px] font-semibold text-zinc-400 uppercase tracking-wider mb-2 px-1">
            Templates
          </div>
          <div className="space-y-1">
            {templates.map(tmpl => {
              const TIcon = tmpl.icon;
              return (
                <button
                  key={tmpl.type}
                  onClick={() => handleTemplateClick(tmpl.type)}
                  className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-left hover:bg-zinc-50 transition-colors group"
                >
                  <div className={cn('w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0', tmpl.bg)}>
                    {TIcon && <TIcon size={14} className={tmpl.color} />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-[13px] font-medium text-zinc-800">{tmpl.name}</div>
                    {tmpl.description && (
                      <div className="text-[11px] text-zinc-400 truncate">{tmpl.description}</div>
                    )}
                  </div>
                  <ChevronRight size={14} className="text-zinc-300 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Divider */}
      {templates?.length > 0 && <div className="mx-4 my-2 border-t border-zinc-100" />}

      {/* Items list */}
      <div className="flex-1 overflow-y-auto px-3 pb-3">
        <div className="text-[10px] font-semibold text-zinc-400 uppercase tracking-wider mb-2 px-1">
          {title || 'Saved'}
        </div>
        {items.length === 0 ? (
          <div className="px-1 py-6 text-center">
            <p className="text-[12px] text-zinc-400">No {(title || 'items').toLowerCase()} yet</p>
            <p className="text-[11px] text-zinc-300 mt-1">Create one using the chat</p>
          </div>
        ) : (
          <div className="space-y-0.5">
            {items.map(item => (
              <div
                key={item.id}
                className={cn(
                  'flex items-center gap-2 px-2.5 py-2 rounded-lg cursor-pointer transition-all group',
                  activeId === item.id
                    ? 'bg-accent/5 border-l-2 border-accent'
                    : 'hover:bg-zinc-50 border-l-2 border-transparent',
                )}
                onClick={() => handleItemClick(item.id)}
              >
                <div className="min-w-0 flex-1">
                  <div className="text-[13px] text-zinc-800 truncate">{item.name || 'Untitled'}</div>
                  <div className="text-[11px] text-zinc-400 truncate">
                    {item.report_type}{item.scope && item.scope !== 'org' ? ` · ${item.scope}` : ''}
                  </div>
                </div>
                {onDeleteItem && (
                  <button
                    onClick={e => { e.stopPropagation(); onDeleteItem(item.id); }}
                    className="opacity-0 group-hover:opacity-100 p-1 text-zinc-300 hover:text-red-500 transition-all"
                  >
                    <Trash2 size={12} />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Extra content (KPI panel etc.) */}
      {extraContent}

      {/* New Chat button */}
      {chat && (
        <div className="px-3 py-2.5 border-t border-zinc-100">
          <button
            onClick={handleNewChat}
            className="w-full flex items-center justify-center gap-1.5 px-3 py-2 text-[12px] font-medium text-zinc-500 hover:text-zinc-700 bg-zinc-50 hover:bg-zinc-100 rounded-lg transition-colors"
          >
            <Plus size={14} />
            New Chat
          </button>
        </div>
      )}
    </div>
  );
}
